import {
  CircleAlert,
  CircleCheck,
  CloudUpload,
  FileQuestionMark,
  LoaderCircle,
  Paperclip,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeProps } from '@/components/ui/badge';
import type { UploadStatus } from '@hooks/useDocumentUpload';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

export interface UploadStatusPresentation {
  Icon: LucideIcon;
  iconClass: string;
  badgeVariant: BadgeVariant;
  /** Outlined badges carry no hue of their own, so they name one here. */
  badgeClass?: string;
  labelKey: string;
}

/**
 * Only `error` gets a filled badge: in a list of ten files every other state is
 * something you skim past, and ten filled badges read as ten alarms.
 */
export const UPLOAD_STATUS_PRESENTATION: Record<UploadStatus, UploadStatusPresentation> = {
  pending: {
    Icon: Paperclip,
    iconClass: 'text-muted-foreground',
    badgeVariant: 'outline',
    badgeClass: 'text-muted-foreground',
    labelKey: 'upload.status.pending',
  },
  uploading: {
    Icon: CloudUpload,
    iconClass: 'text-primary',
    badgeVariant: 'outline',
    badgeClass: 'border-primary/40 text-primary',
    labelKey: 'upload.status.uploading',
  },
  processing: {
    Icon: LoaderCircle,
    iconClass: 'animate-spin text-primary motion-reduce:animate-none',
    badgeVariant: 'outline',
    badgeClass: 'border-primary/40 text-primary',
    labelKey: 'upload.status.processing',
  },
  completed: {
    Icon: CircleCheck,
    iconClass: 'text-success',
    badgeVariant: 'outline',
    badgeClass: 'border-success/40 text-success',
    labelKey: 'upload.status.completed',
  },
  error: {
    Icon: CircleAlert,
    iconClass: 'text-destructive',
    badgeVariant: 'destructive',
    labelKey: 'upload.status.error',
  },
  duplicate: {
    Icon: TriangleAlert,
    iconClass: 'text-warning',
    badgeVariant: 'outline',
    badgeClass: 'border-warning/40 text-warning',
    labelKey: 'upload.status.duplicate',
  },
};

const UPLOAD_STATUS_UNKNOWN: UploadStatusPresentation = {
  Icon: FileQuestionMark,
  iconClass: 'text-muted-foreground',
  badgeVariant: 'outline',
  badgeClass: 'text-muted-foreground',
  labelKey: 'upload.status.unknown',
};

/** Keeps the old `default:` branch of the three switches alive for a status
 *  that reaches the UI from outside the union (polled server state). */
export const uploadStatusPresentation = (status: UploadStatus): UploadStatusPresentation =>
  UPLOAD_STATUS_PRESENTATION[status] ?? UPLOAD_STATUS_UNKNOWN;
